import { Box, Group, Text, Tooltip } from '@mantine/core'
import { QuestionIcon } from '@phosphor-icons/react'
import { IconGlyph } from './Icons'

export type RouteHeaderLabelTone = 'challenge' | 'neutral' | 'practice'

export function RouteHeaderLabel({
  onClick,
  text,
  tone = 'neutral',
}: {
  onClick?: () => void
  text: string
  tone?: RouteHeaderLabelTone
}) {
  const toneStyles =
    tone === 'practice'
      ? {
          background: 'rgba(32, 101, 98, 0.26)',
          border: '1px solid rgba(109, 209, 204, 0.24)',
          color: '#6dd1cc',
        }
      : tone === 'challenge'
        ? {
            color: '#f0a35c',
          }
        : {
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(242, 232, 217, 0.16)',
            color: '#d8ccb6',
          }

  const background = toneStyles.background ?? (onClick ? 'transparent' : undefined)

  return (
    <Text
      aria-label={onClick ? 'Back to challenge' : undefined}
      bd={toneStyles.border}
      bg={background}
      bdrs={8}
      c={toneStyles.color}
      data-testid="boggle-gameplay-label"
      ff="Tilt Neon, sans-serif"
      fz={tone === 'challenge' ? 17 : 15}
      fw={700}
      lh={1}
      lts="0.12em"
      onClick={onClick}
      px={6}
      py={6}
      style={
        onClick
          ? {
              cursor: 'pointer',
              pointerEvents: 'all',
            }
          : { pointerEvents: 'none' }
      }
      tt="uppercase"
      w="fit-content"
    >
      {text}
    </Text>
  )
}

export function GameModePill({
  color,
  description,
  name,
  withInfoIcon = true,
  withTooltip = true,
  fz = 28,
}: {
  color: string
  description: string
  name: string
  withInfoIcon?: boolean
  withTooltip?: boolean
  fz?: number
}) {
  const pill = (
    <Group pos="relative">
      <Box
        aria-label={withTooltip ? `${name}: ${description}` : undefined}
        bdrs={50}
        bd={`1px solid transparent`}
        bg="transparent"
        c={color}
        component={withTooltip ? 'button' : 'span'}
        fz={fz}
        fw={700}
        lh={1}
        style={{
          cursor: withTooltip ? 'help' : 'default',
          fontFamily: "'Tilt Neon', sans-serif",
          lineHeight: 1,
          textShadow: '1px 1px 1px black, 0 0 10px rgba(255, 255, 255, 0.2)',
        }}
        {...(withTooltip ? { type: 'button' as const } : {})}
      >
        {name}
      </Box>
      {withInfoIcon ? (
        <Box
          c="rgba(255, 255, 255, 0.5)"
          opacity={0.5}
          pos="absolute"
          right={-26}
          style={{ transform: 'translateY(-50%)' }}
          top="50%"
        >
          <IconGlyph color="white" icon={QuestionIcon} size={20} weight="fill" />
        </Box>
      ) : null}
    </Group>
  )

  return withTooltip ? (
    <Tooltip
      bg="dark.8"
      events={{ hover: true, focus: true, touch: true }}
      ff="Tilt Neon, sans-serif"
      fz={15}
      label={description}
      lh={1.3}
      maw={260}
      multiline
      position="bottom"
      ta="center"
      withArrow={false}
      withinPortal
    >
      {pill}
    </Tooltip>
  ) : (
    pill
  )
}
